<template>
    <Overlay
        :show="showOverlay"
    >
        <b-row class="pb-2" v-if="showHeading">
            <b-col>
                <h4 v-if="null === id">
                    {{ $t('Page(SupplierCard):Component(Text):add') }}
                </h4>
                <h4 v-else>
                    {{ $t('Page(SupplierCard):Component(Text):edit', {organization: organization.title}) }}
                </h4>
            </b-col>
            <b-col md="6" lg="6">
                <Actions
                    class="float-right"
                    :is-save-disabled="isAddButtonDisabled"
                    @save="save"
                    @close="close"
                />
            </b-col>
        </b-row>
        <b-collapse :visible="showExistingSupplierMessage">
            <b-alert
                :variant="existingSupplierMessageVariant"
                show
            >
                <div class="alert-body">
                    {{ existingSupplierMessage }}
                </div>
            </b-alert>
        </b-collapse>
        <b-collapse :visible="showExistingSubscriberMessage">
            <b-alert
                variant="warning"
                show
            >
                <div class="alert-body">
                    <div class="mb-1">
                        {{ existingSubscriberOrganizationMessage }}
                    </div>
                    <v-select
                        v-model="selectedLookupItem"
                        :clearable="false"
                        :options="subscriberLookupResult"
                        :getOptionLabel="(item) => item.organization.title"
                    />
                    <div v-if="selectedLookupItem" class="pt-1 text-right">
                        <Button class="btn-sm" @click="useExistingSubscriberOrganization">
                            {{ $t('Component(Button):use-existing-subscriber-organization') }}
                        </Button>
                    </div>
                </div>
            </b-alert>
        </b-collapse>
        <b-tabs>
            <b-tab :title="$t('Page(OrganizationCard):Component(Tab):general')">
                <b-row>
                    <b-col>
                        <GeneralInfo
                            ref="generalInfo"
                            type="supplier"
                            :organization="organization"
                            :is-parent-visible="false"
                            :show-valid-from-to-dates="false"
                            :is-location-visible="true"
                            @input="onGeneralInfoChange"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <Contacts
                            :expected-contact-domain="organization.domain"
                            :selected="organization.contacts"
                            :remove-action-visible="isContactRemoveButtonVisible"
                            @save="onContactSave"
                            @remove="onRemoveContact"
                        />
                    </b-col>
                </b-row>
            </b-tab>

            <b-tab v-if="id" :title="$t('Page(ProductionUnits):Component(Text):page-label')">
                <Grid :supplier-id="id"/>
            </b-tab>
        </b-tabs>
    </Overlay>
</template>

<script>
import vSelect from 'vue-select'
import {BRow, BAlert, BCol, BCollapse, BTabs, BTab} from 'bootstrap-vue'
import {Actions, GeneralInfo} from "@/views/administration/organization/components";
import {ProductCategorySelect} from "@core/components/product";
import Contacts from "@/views/administration/organization/components/Contacts";
import {SubscriberApi, SupplyChain} from "@core/api";
import backendResponse from "@core/mixins/ui/backend-response";
import Button from "@core/components/common/Button.vue";
import Overlay from "@core/components/common/Overlay.vue";
import {CommunicationType} from "@/libs/supply-chain";
import Grid from "@/views/administration/organization/production-unit/Grid.vue";

export default {
    mixins: [backendResponse],
    components: {
        Overlay, Button, BAlert, BRow, BCol, Actions, GeneralInfo, ProductCategorySelect, Contacts, BCollapse,
        vSelect, BTabs, BTab, Grid,
    },

    props: {
        id: {
            default: null
        },
        showHeading: {
            default: true
        },
        isRoot: {
            default: false
        }
    },

    data() {
        return {
            showOverlay: true,

            organizations: [],
            organization: {
                title: null,
                state: null,
                domain: null,
                legalIdentity: null,
                contacts: [],
                parent: null,
                location: {
                    address: null,
                    city: null,
                    zip: null,
                    coordinates: {
                        latitude: null,
                        longitude: null
                    },
                    unlocode: null
                },
            },
            contactIdIncrement: 1,

            showExistingSupplierMessage: false,
            existingSupplierMessage: false,
            existingSupplierMessageVariant: 'danger',
            hasDuplicationControlErrors: false,

            showExistingSubscriberMessage: false,
            isSubscriberFullyMatched: false,
            subscriberLookupResult: [],
            selectedLookupItem: null,

            generalInfoTimeoutId: null
        }
    },

    methods: {
        isExistingSupplierCurrentSupplier(supplier) {
            return this.id && parseInt(this.id) === supplier.id;
        },
        checkSuppliersDomain() {
            return this
                .searchExistingSupplier({domain: this.organization.domain})
                .then((data) => {
                    if (data.exists) {
                        if (this.isExistingSupplierCurrentSupplier(data.supplier)) {
                            this.showExistingSupplierMessage = false;
                            return;
                        }
                        this.showExistingSubscriberMessage = false;
                        this.showExistingSupplierMessage = true;
                        this.existingSupplierMessageVariant = 'warning';
                        this.existingSupplierMessage = this.$t('Page(SupplierCard):Component(Text):supplier-with-domain-exists', {
                            name: data.supplier.title,
                            domain: data.supplier.domain
                        });
                    } else {
                        this.showExistingSupplierMessage = false;
                        this.lookupSubscriber();
                    }
                })
                .finally(() => this.showLoading = false);
        },
        lookupSubscriber() {
            return SubscriberApi
                .lookup(this.organization.title, this.organization.domain, this.organization.legalIdentity)
                .then(r => {
                    const data = r.data;
                    this.isSubscriberFullyMatched = false;
                    this.showExistingSubscriberMessage = false;
                    this.isSubscriberFullyMatched = data.fullMatch;

                    if (data.items.length > 0) {
                        this.showExistingSubscriberMessage = true;
                        this.selectedLookupItem = data.items[0];
                        this.subscriberLookupResult = data.items;

                        //  if full match by title or legal identity then supplier can not be created
                        if (this.isSubscriberFullyMatched && (data.metadata.matchTitle || data.metadata.matchLegalIdentity)) {
                            this.hasDuplicationControlErrors = true;
                        }
                    }

                    if (this.isSubscriberFullyMatched) {
                        if (this.isSubscriberCorrectToSubscriberOrganization(this.selectedLookupItem.organization)) {
                            this.hasDuplicationControlErrors = false;
                            this.showExistingSubscriberMessage = false;
                        } else {
                            this.hasDuplicationControlErrors = true;
                        }
                    }
                })
            ;
        },
        isSubscriberCorrectToSubscriberOrganization(subscriberOrganization) {
            if (!this.organization.legalIdentity || !this.organization.legalIdentity.type || !this.organization.legalIdentity.value) {
                return false;
            }

            return undefined !== subscriberOrganization.legalIdentities.find(le => {
                return le.type.id === this.organization.legalIdentity.type.id && le.value === this.organization.legalIdentity.value;
            });
        },
        checkExistingSupplier() {
            this.showLoading = true;
            if (!this.organization.title && !this.organization.legalIdentity) {
                this.checkSuppliersDomain();
            } else {
                this
                    .searchExistingSupplier({
                        title: this.organization.title,
                        legalIdentity: this.organization.legalIdentity
                    })
                    .then(data => {
                        this.hasDuplicationControlErrors = false;
                        if (data.exists) {
                            if (this.isExistingSupplierCurrentSupplier(data.supplier)) {
                                if (this.organization.domain) {
                                    return this.checkSuppliersDomain();
                                }
                                this.showExistingSupplierMessage = false;
                                return;
                            }
                            this.showExistingSubscriberMessage = false;
                            this.showExistingSupplierMessage = true;
                            this.existingSupplierMessageVariant = 'danger';
                            this.hasDuplicationControlErrors = true;
                            this.existingSupplierMessage = this.$t('Page(SupplierCard):Component(Text):supplier-with-name-or-id-exists');
                        } else if (this.organization.domain) {
                            return this.checkSuppliersDomain();
                        } else {
                            this.showExistingSupplierMessage = false;
                            return this.lookupSubscriber();
                        }
                    })
                    .finally(() => this.showLoading = false)
                ;
            }
        },
        useExistingSubscriberOrganization() {
            this.organization.title = this.selectedLookupItem.organization.title;
            this.organization.legalIdentity = this.selectedLookupItem.organization.legalIdentities[0];

            if (!this.organization.domain && this.selectedLookupItem.organization.domains.length > 0) {
                this.organization.domain = this.selectedLookupItem.organization.domains[0];
            }
            this.hasDuplicationControlErrors = false;
            this.showExistingSubscriberMessage = false;

            //  hack
            this.organization.__ob__.dep.notify()

            //  check if existing
            this.checkExistingSupplier();
        },

        searchExistingSupplier(supplier) {
            return SupplyChain.exists(supplier).then(r => r.data);
        },

        onGeneralInfoChange(info) {
            this.organization.title = info.title;
            this.organization.domain = info.domain;
            this.organization.legalIdentity = info.legalIdentity;
            this.organization.state = info.state;
            this.organization.location = info.location;

            clearTimeout(this.generalInfoTimeoutId);
            this.generalInfoTimeoutId = setTimeout(() => {
                this.checkExistingSupplier();
            }, 1500)
        },
        onRemoveContact(contact) {
            let index = this.organization.contacts.findIndex(c => c.id === contact.id);

            if (index >= 0) {
                this.organization.contacts.splice(index, 1);
            }
        },
        onContactSave(id, contact) {
            if (!id) {
                contact.id = (this.contactIdIncrement++) + 'id';
                contact.notPersisted = true;

                this.organization.contacts.push(contact);
            } else {
                let index = this.organization.contacts.findIndex(o => o.id === id);

                if (index !== -1) {
                    let organizationContact = this.organization.contacts[index];
                    organizationContact.name = contact.name;
                    organizationContact.position = contact.position;
                    organizationContact.description = contact.description;
                    organizationContact.communicationChannels = contact.communicationChannels;
                }
            }
        },
        gatherSaveAttributes() {
            let organization = {
                ...this.organization,
                state: this.organization.state.id,
                contacts: this.organization.contacts.map(c => {
                    if (c.notPersisted) {
                        let contact = {...c};
                        delete contact['id'];
                        delete contact['notPersisted'];
                        return contact;
                    }
                    return c;
                })
            };

            if (organization.legalIdentity.type) {
                organization.legalIdentity = {
                    type: organization.legalIdentity.type.id,
                    value: organization.legalIdentity.value
                };
            }

            return organization;
        },
        save() {
            Promise.all([
                this.$refs.generalInfo.validate()
            ]).then((validationResults) => {
                if (validationResults.includes(false)) {
                    return;
                }
                this.showOverlay = true;

                let organization = this.gatherSaveAttributes();
                this
                    .saveSupplier(organization)
                    .then((r) => {
                        if (this.id) {
                            return this.fetch();
                        }

                        if (r.data.id) {
                            this.onCreate(r.data.id);
                        }
                    })
                    .catch(this.handleBackendError)
                    .finally(() => {
                        this.showOverlay = false;
                    })
                ;
            });
        },
        onCreate(id) {
            this.$router.push({
                name: 'administration-supplier-edit',
                params: {type: 'supplier', id: id}
            });
        },
        close() {
            this.$router.push({name: 'administration-suppliers'})
        },
        fetch() {
            return SupplyChain
                .getOrganizationCard(this.id)
                .then(r => {
                    this.organization = r.data;
                });
        },
        saveSupplier(supplier) {
            if (!this.id) {
                return SupplyChain
                    .newSupplier(supplier)
                    .then(r => {
                        this.showDefaultSuccessMessage('Organization has been successfully created');
                        return r;
                    });
            } else {
                return SupplyChain
                    .updateSupplier(supplier)
                    .then(r => {
                        this.showDefaultSuccessMessage('Organization has been successfully updated');
                        return r;
                    });
            }
        },
    },

    computed: {
        existingSubscriberOrganizationMessage() {
            if (this.isSubscriberFullyMatched) {
                return this.$t('Component(SupplierSelector):subscriber-exists', {
                    name: this.selectedLookupItem.organization.title
                });
            }

            return this.$t('Component(SupplierSelector):subscriber-partially-matched');
        },
        isAddButtonDisabled() {
            //  disabled when
            //  - does not meet duplicate control
            //  - contacts are empty
            //  - at least one email has to be presented
            //  - state has not set
            if (!this.organization.state) {
                return true;
            }
            if (this.organization.contacts.length === 0) {
                return true;
            }
            const communicationChannelsLength = this.organization.contacts.map(c => c.communicationChannels.length);
            if (communicationChannelsLength.includes(0)) {
                return true;
            }
            const hasEmail = this.organization.contacts.map(c => {
                return c.communicationChannels.find(channel => {
                    return channel.type === CommunicationType.EMAIL && channel.channel;
                });
            });
            if (hasEmail.includes(undefined)) {
                return true;
            }
            if (!this.organization.title || !this.organization.domain || !this.organization.legalIdentity) {
                return true;
            }
            if (!this.organization.legalIdentity.type || !this.organization.legalIdentity.value) {
                return true;
            }
            return this.hasDuplicationControlErrors;
        },
        isContactRemoveButtonVisible() {
            return this.organization.contacts.length > 1;
        }
    },

    watch: {
        id() {
            this.fetch().then(() => this.showOverlay = false);
        }
    },

    created() {
        if (this.id) {
            this.fetch().then(() => this.showOverlay = false);
        } else {
            setTimeout(() => this.showOverlay = false, 500);
        }
    }
}
</script>
